/**
 * Transfer analytics — `/api/bi/transfers/*`.
 */
import { useQuery } from '@tanstack/react-query';
import { get } from '@/api/client';

export interface TransfersOverview {
  range: { start: string; end: string };
  kpis: {
    transfers: number;
    accepted: number;
    in_flight: number;
    stuck_over_24h: number;
    units_in_transit: number;
    /** Cost basis; kept for back-compat and equal to `value_in_transit_at_cost`. */
    value_in_transit: number;
    value_in_transit_at_cost: number;
    value_in_transit_at_retail: number;
    requests: number;
    requests_pending: number;
    requests_completed: number;
    request_fulfillment_rate: number;
  };
  by_status: Array<{ status: string; transfers: number }>;
  by_direction: Array<{
    source_type: string;
    destination_type: string;
    transfers: number;
  }>;
  by_day: Array<{ day: string | null; transfers: number; accepted: number }>;
  by_lane: Array<{
    source_store_id: number;
    source_store_name: string;
    destination_store_id: number;
    destination_store_name: string;
    transfers: number;
    accepted: number;
    in_flight: number;
  }>;
  requests_by_status: Array<{ status: string; requests: number }>;
}

export interface TransfersCycleTime {
  range: { start: string; end: string };
  sample_accepted: number;
  events_covered_transfers: number;
  note: string;
  native_medians_hours: {
    created_to_received: number | null;
    received_to_accepted: number | null;
    created_to_accepted: number | null;
  };
  stage_medians_hours: Record<string, number | null>;
}

export interface TransfersRequests {
  range: { start: string; end: string };
  kpis: {
    requests: number;
    pending: number;
    in_progress: number;
    completed: number;
    linked_to_transfer: number;
    link_coverage: number;
    lines: number;
    units_requested: number;
    lines_completed: number;
    line_completion_rate: number;
  };
  by_status: Array<{ status: string; requests: number }>;
  by_lane: Array<{
    source_store_id: number;
    source_store_name: string;
    destination_store_id: number;
    destination_store_name: string;
    requests: number;
    completed: number;
    linked: number;
  }>;
  oldest_pending: Array<{
    id: number;
    source_store_id: number;
    source_store_name: string;
    destination_store_id: number;
    destination_store_name: string;
    created_at: string | null;
    fulfilled_by_transfer_id: number | null;
  }>;
  note: string;
}

export interface TransfersDiscrepancies {
  range: { start: string; end: string };
  /** Row cap on the detail lists below; KPIs cover the full set. */
  detail_limit: number;
  kpis: {
    request_shortfall_lines: number;
    request_shortfall_units: number;
    transfer_shortfall_lines: number;
    transfer_shortfall_units: number;
  };
  request_shortfalls: Array<{
    request_item_id: number;
    request_id: number;
    product_id: number;
    product_name: string;
    source_store_id: number;
    source_store_name: string;
    destination_store_id: number;
    destination_store_name: string;
    requested: number;
    scanned: number;
    shortfall: number;
    completed: boolean;
  }>;
  transfer_shortfalls: Array<{
    transfer_item_id: number;
    transfer_id: number;
    identifier: string;
    product_id: number;
    product_name: string;
    source_store_id: number;
    source_store_name: string;
    destination_store_id: number;
    destination_store_name: string;
    transfer_status: string;
    item_status: string;
    declared: number;
    scanned: number;
    shortfall: number;
  }>;
}

export interface TransfersVendorReturns {
  range: { start: string; end: string };
  kpis: {
    returns: number;
    lines: number;
    units: number;
    value: number;
  };
  by_vendor: Array<{
    vendor_id: number;
    vendor_name: string;
    returns: number;
    lines: number;
    units: number;
    value: number;
  }>;
  by_day: Array<{
    day: string | null;
    returns: number;
    units: number;
    value: number;
  }>;
  top_skus: Array<{
    product_id: number;
    product_name: string;
    lines: number;
    units: number;
    value: number;
  }>;
}

function rangeParams(f: {
  start?: string | null;
  end?: string | null;
  storeId?: number | null;
}) {
  return {
    start: f.start ?? undefined,
    end: f.end ?? undefined,
    store_id: f.storeId ?? undefined,
  };
}

type RangeFilters = {
  start?: string | null;
  end?: string | null;
  storeId?: number | null;
};

export function useTransfersOverview(f: RangeFilters) {
  return useQuery({
    queryKey: ['bi', 'transfers', 'overview', f.start ?? null, f.end ?? null, f.storeId ?? null],
    queryFn: () =>
      get<TransfersOverview>('/api/bi/transfers/overview/', { params: rangeParams(f) }),
  });
}

export function useTransfersCycleTime(f: RangeFilters) {
  return useQuery({
    queryKey: [
      'bi',
      'transfers',
      'cycle-time',
      f.start ?? null,
      f.end ?? null,
      f.storeId ?? null,
    ],
    queryFn: () =>
      get<TransfersCycleTime>('/api/bi/transfers/cycle-time/', { params: rangeParams(f) }),
  });
}

export function useTransfersRequests(f: RangeFilters) {
  return useQuery({
    queryKey: ['bi', 'transfers', 'requests', f.start ?? null, f.end ?? null, f.storeId ?? null],
    queryFn: () =>
      get<TransfersRequests>('/api/bi/transfers/requests/', { params: rangeParams(f) }),
  });
}

export function useTransfersDiscrepancies(f: RangeFilters) {
  return useQuery({
    queryKey: [
      'bi',
      'transfers',
      'discrepancies',
      f.start ?? null,
      f.end ?? null,
      f.storeId ?? null,
    ],
    queryFn: () =>
      get<TransfersDiscrepancies>('/api/bi/transfers/discrepancies/', {
        params: rangeParams(f),
      }),
  });
}

export function useTransfersVendorReturns(f: RangeFilters) {
  return useQuery({
    queryKey: [
      'bi',
      'transfers',
      'vendor-returns',
      f.start ?? null,
      f.end ?? null,
      f.storeId ?? null,
    ],
    queryFn: () =>
      get<TransfersVendorReturns>('/api/bi/transfers/vendor-returns/', {
        params: rangeParams(f),
      }),
  });
}
